import type { ScanCatalog, SuggestedCode } from "@hobbyshopify/backend-client";
import { CODE_ORIGIN_LABEL, codeKey } from "../core/primitives/catalog";

/**
 * The codes on a scan and what the shared catalogue says they are.
 *
 * A ticked code is the shop's answer to every other shop scanning it, which is
 * why nothing the model read is ticked for the reviewer. When shops disagree,
 * picking one of their products is what settles it here.
 */
export function CatalogCodes({
  catalog,
  codes,
  approved,
  picking,
  disabled,
  onToggle,
  onPick,
}: {
  catalog: ScanCatalog | null;
  codes: readonly SuggestedCode[];
  approved: readonly string[];
  /** The catalogue product a pick is in flight for. */
  picking: string | null;
  disabled: boolean;
  onToggle: (key: string) => void;
  onPick: (catalogProductId: string) => void;
}) {
  if (codes.length === 0) return null;
  const conflict = catalog?.status === "CONFLICT";
  return (
    <fieldset className="mb-5 space-y-2" disabled={disabled}>
      <legend className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Barcodes</legend>
      {conflict && (
        <div className="note note-warn space-y-2 text-sm">
          <p>Shops disagree about what this code is. Pick the right product, or approve the code for this one.</p>
          {catalog.candidates.map((candidate) => {
            const picked = catalog.product?.id === candidate.id;
            return (
              <button
                key={candidate.id}
                type="button"
                onClick={() => onPick(candidate.id)}
                disabled={picking !== null}
                aria-pressed={picked}
                className={`flex w-full items-center gap-3 rounded-row border p-2 text-left ${
                  picked ? "border-accent bg-accent/10" : "border-line bg-surface hover:border-muted"
                }`}
              >
                <div className="h-12 w-9 shrink-0 overflow-hidden rounded border border-line bg-surface-strong">
                  {candidate.imageUrl && <img src={candidate.imageUrl} alt="" className="h-full w-full object-cover" />}
                </div>
                <span className="min-w-0 flex-1 truncate text-white">{candidate.title}</span>
                <span className="badge badge-neutral">
                  {candidate.votes} {candidate.votes === 1 ? "shop" : "shops"}
                </span>
                {picking === candidate.id && <span className="text-xs text-faint">Picking…</span>}
              </button>
            );
          })}
        </div>
      )}
      {catalog?.status === "HIT" && catalog.product && (
        <p className="text-xs text-faint">Shared catalogue: {catalog.product.title}</p>
      )}
      {codes.map((code) => {
        const key = codeKey(code);
        return (
          <label key={key} className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={approved.includes(key)} onChange={() => onToggle(key)} />
            <span className="font-mono text-white">{code.value}</span>
            <span className="text-xs uppercase text-faint">{code.type}</span>
            <span className={`badge ${code.origin === "DEVICE" ? "badge-neutral" : "badge-warn"}`}>
              {CODE_ORIGIN_LABEL[code.origin]}
            </span>
          </label>
        );
      })}
      <p className="text-xs text-faint">
        Ticked codes are saved as your shop's answer in the shared catalogue. Prices, costs and stock stay private.
      </p>
    </fieldset>
  );
}
